import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useLayoutEffect,
} from 'react'
import { Okpd2 } from '../../../../../Org/Handbook/types'
import { OrganizationInfo } from '../../../../../Org/Home/types'

import { ContractPerformance, Country } from '../../../../types'
import { CurrencyList } from '../../../types'

import {
    ContractPerformancePayment,
    ContractPerformancePaymentInit,
} from '../../types'

import HoverTooltip from '../../../../../../../components/HoverTooltip'

type Props = {
    perfAction: boolean
    performanceGuid: string | undefined
    perfPaymentNumber: number
    theme: string
    performancePayment: ContractPerformancePayment
    selected: boolean
    onClick: (perfPayment_guid: string) => void
    onRightClick: (perfPayment_guid: string) => void
    onClickEdit: (perfPayment_guid: string) => void
    onDeleteRow: (perfPayment_guid: string) => void
}

function formatCountryOfOriginStrict(src: Country | null | undefined): string {
    const list = src?.country ?? []
    if (!Array.isArray(list) || list.length === 0) return ''

    const names = list.map((c) => (c?.name ?? '').trim()).filter(Boolean)

    // убрать дубли, сохраняя порядок
    const seen = new Set<string>()
    const norm = (s: string) => s.toLocaleLowerCase('ru-RU')
    const unique = names.filter((n) => {
        const k = norm(n)
        if (seen.has(k)) return false
        seen.add(k)
        return true
    })

    // РФ — первой
    const rfIndex = unique.findIndex((n) => norm(n) === 'российская федерация')
    if (rfIndex > 0) {
        const [rf] = unique.splice(rfIndex, 1)
        unique.unshift(rf)
    }

    return unique.join(', ')
}

function getCountryListOrderedStrict(
    src: Country | null | undefined
): string[] {
    const list = src?.country ?? []
    if (!Array.isArray(list) || list.length === 0) return []

    const names = list.map((c) => (c?.name ?? '').trim()).filter(Boolean)

    // dedupe (без регистра), сохранив порядок
    const seen = new Set<string>()
    const norm = (s: string) => s.toLocaleLowerCase('ru-RU')
    const unique = names.filter((n) => {
        const k = norm(n)
        if (seen.has(k)) return false
        seen.add(k)
        return true
    })

    // РФ — первой
    const rfIndex = unique.findIndex((n) => norm(n) === 'российская федерация')
    if (rfIndex > 0) {
        const [rf] = unique.splice(rfIndex, 1)
        unique.unshift(rf)
    }

    return unique
}

function purchaseTypeLabel(code?: string | null): string {
    switch ((code ?? '').toUpperCase()) {
        case 'G':
            return 'Товар'
        case 'W':
            return 'Работа'
        case 'S':
            return 'Услуга'
        default:
            return '' // неизвестно/пусто
    }
}

function formatTotalCount(totalCount: number) {
    // Получаем число с 5 знаками после запятой (с округлением)
    let str = totalCount.toFixed(5)
    // Если есть десятичная часть, удаляем лишние нули
    if (str.indexOf('.') !== -1) {
        // Удаляем концевые нули
        str = str.replace(/0+$/, '')
        // Если после удаления нулей осталась точка, убираем её
        str = str.replace(/\.$/, '')
    }
    // Заменяем точку на запятую
    return str.replace('.', ',')
}

const formatNumber = (num: number | string | null | undefined): string => {
    if (num === null || num === undefined) return '-' // Обрабатываем пустые значения

    const parsedNum = typeof num === 'number' ? num : parseFloat(num)

    if (isNaN(parsedNum)) {
        return '-' // Обрабатываем некорректные значения
    }

    return parsedNum
        .toFixed(2) // Два знака после запятой
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ') // Разделяем тысячные пробелами
        .replace('.', ',') // Заменяем точку на запятую
}

const PerformancePaymentTableRow: React.FC<Props> = (props) => {
    return (
        <div className="flex flex-col">
            {/* строка оплаты */}
            <div
                className={
                    props.perfAction
                        ? 'flex flex-row gap-1 mx-1 rounded text-[11px] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]'
                        : props.selected
                          ? 'plan-position-table-row-selected'
                          : 'plan-position-table-row'
                }
                onClick={() => {
                    if (props.perfAction == false) {
                        props.onClick(
                            props.performancePayment
                                .performance_contract_position_guid
                        )
                    }
                }}
                onContextMenu={(e) => {
                    e.preventDefault() // Отключает стандартное контекстное меню
                    if (props.perfAction == false) {
                        props.onRightClick(
                            props.performancePayment
                                .performance_contract_position_guid
                        )
                    }
                }}
                // onDoubleClick={() => props.onDoubleClick(props.performance.guid)}
            >
                {props.perfAction ? (
                    <div className="flex w-8 h-[40px] items-center justify-center text-center"></div>
                ) : (
                    <div className="flex w-8 h-[40px] items-center justify-center text-center">
                        <input
                            type="checkbox"
                            checked={props.selected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => {
                                props.onClick(
                                    props.performancePayment
                                        .performance_contract_position_guid
                                )
                            }}
                        />
                    </div>
                )}
                <div className="flex w-8 items-center justify-center text-center ">
                    {props.perfPaymentNumber}
                </div>
                <div className="flex flex-1 items-center justify-start text-left">
                    {`Платежное поручение № ${
                        props.performancePayment.annotation
                            ? props.performancePayment.annotation
                            : '-'
                    }`}
                </div>
                <div className="flex w-[1px] h-full items-center justify-center text-center rounded bg-[#464447]"></div>
                <div className="flex w-[215px] text-left items-center justify-center">
                    {}
                </div>
                <div className="flex w-[1px] h-full items-center justify-center text-center rounded bg-[#464447]"></div>
                <div className="flex flex-row w-[215px] text-left items-center justify-center">
                    <div
                        className={`flex w-[95px] p-1 items-center justify-center text-center rounded`}
                    >
                        {props.performancePayment.payment_date
                            ?.split('-')
                            .reverse()
                            .join('-')
                            .replace(/[\-]/g, '.')}
                    </div>
                    <div
                        className={`flex w-[120px] p-1 items-center justify-end text-right rounded`}
                    >
                        {formatNumber(props.performancePayment.price)}
                    </div>
                </div>
                <div className="flex w-[1px] h-full items-center justify-center text-center rounded bg-[#464447]"></div>
                <div className="flex w-8 text-right items-center justify-center"></div>
                {props.perfAction ? (
                    <>
                        <div className="flex w-8 text-right items-center justify-center"></div>
                        <div className="flex w-8 text-right items-center justify-center"></div>
                    </>
                ) : (
                    <>
                        <div className="flex w-8 text-right items-center justify-center">
                            <HoverTooltip tooltipText="Редактировать">
                                <button
                                    onClick={() =>
                                        props.onClickEdit(
                                            props.performancePayment.guid ?? ''
                                        )
                                    }
                                    className="w-6 h-6 grid rounded-full items-center justify-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="edit-alt"
                                    >
                                        <path
                                            fill={
                                                props.theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                            }
                                            d="M5,18H9.24a1,1,0,0,0,.71-.29l6.92-6.93h0L19.71,8a1,1,0,0,0,0-1.42L15.47,2.29a1,1,0,0,0-1.42,0L11.23,5.12h0L4.29,12.05a1,1,0,0,0-.29.71V17A1,1,0,0,0,5,18ZM14.76,4.41l2.83,2.83L16.17,8.66,13.34,5.83ZM6,13.17l5.93-5.93,2.83,2.83L8.83,16H6ZM21,20H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Z"
                                        ></path>
                                    </svg>
                                </button>
                            </HoverTooltip>
                        </div>
                        <div className="flex w-8 text-right items-center justify-center">
                            <HoverTooltip tooltipText="Удалить">
                                <button
                                    onClick={() =>
                                        props.onDeleteRow(
                                            props.performancePayment
                                                .performance_contract_position_guid ??
                                                ''
                                        )
                                    }
                                    className=" flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#ed9cae] dark:hover:bg-[#701f1f]"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="trash-alt"
                                    >
                                        <path
                                            fill={
                                                props.theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                            }
                                            d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                        ></path>
                                    </svg>
                                </button>
                            </HoverTooltip>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export default PerformancePaymentTableRow
